import { z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";
import {
  CustomerStatus,
  CustomerTier,
  PreferredChannel,
  PreferredLocale,
  SalesAddress,
  SalesCountry,
  SalesCurrency,
  SalesMinorMoneyNonNegative,
} from "./schemas.js";

// Module 09, THE MERGE's contract DTOs (P3-E2 PR E2-2b; spec 09 §2.30, §2.33,
// §3.13, §3.14, §4.17 and §5's E2-2 rows 15 to 17; D-324 rulings 4 to 16).
// Routes live in `./crm-register.ts` beside the rest of the customer record.
// These shapes live in a file of their own, and not in `./crm-schemas.ts`,
// because the preview restates the customer's profile fields with the
// customer's own field schemas (`SalesAddress`, `CustomerTier` and the rest),
// which `./schemas.ts` declares. `./schemas.ts` imports `./crm-schemas.ts`, so
// that file stays a leaf, and an import back from it would be a module cycle
// whose consts read before they exist. This file imports `./schemas.ts` and
// nothing imports this file but the register, the index and the event
// catalog, so it closes no cycle.
//
// WHAT THIS FILE HOLDS, AND THE RULES IT RESTATES BECAUSE THEY BITE HERE:
//  * THE PREVIEW IS A DRY RUN. `POST /v1/customers/{id}/merge-preview` writes
//    nothing. The customer in the path is the winner and `loser_id` names the
//    other. `winner_version` and `loser_version` are each row's `xmin` as
//    text, D-318's instrument, and the merge compares both under its locks
//    (§3.13).
//  * WHO SEES WHAT. Only a caller on the group_admin or facility_admin
//    lineage reaches these doors (§7 narrowing 18). Inside them the legal name
//    follows `LegalNameReach` and the credit limit follows the customer
//    card's predicate (`customerVisibility(...).creditVisible`), so a tenant's
//    clone of facility_admin that holds `sales.read_value` is still served no
//    `credit_limit_minor` (§5's E2-2 items). A commercial attachment is named
//    by id and title to a holder of `sales.read_value` alone, and every other
//    caller is served the count (§7 narrowing 19).
//  * THE ACCESS SET IS PART OF THE VERSION. `add_collaborators` and
//    `skip_collaborators` together name exactly the preview's `gains_access`,
//    and `commercial_attachment_ids` names exactly its
//    `commercial_audience_changes`, or a caller without `sales.read_value`
//    sends `commercial_attachment_count`. The merge recomputes both sets under
//    its locks and answers 409 `MERGE_STALE` when either differs, because an
//    assignment write or an attach leaves the customer's `xmin` as it was.
//  * THE RESULT NESTS NO CUSTOMER. The idempotency store keeps a nested object
//    as sent and the winner's `notes` would stay there (§2.26), so the answer
//    names the winner's id and the web reads the winner's 360 again.
//  * COUNTS ARE COUNTS. Every per-table figure is a non-negative integer and
//    never money.

const MergeCount = z.number().int().min(0);

/**
 * The tables a merge re-parents or copies onto the winner, in the order
 * `customer_merge_moves.table_name`'s CHECK lists them (spec 09 §2.33,
 * migration 0079, and `sales_attachments` last, which migration 0080 adds in
 * P3-E2 PR E2-2a2). Only a customer's own attachments are re-parented. A
 * quote's or an order's attachment names its document and follows it, so it
 * is neither moved nor counted here (§3.13). Module 17's
 * conversations move in the same transaction through
 * `CustomerConversationPort.repointCustomerInTxn` and are module 17's rows, so
 * they are neither recorded in `customer_merge_moves` nor counted here.
 * `quote_sends` joined with P3-K K-3 (migration 0106, D-546 ruling 3): the
 * loser's sends move so §9.17's guard counts them against the winner.
 */
export const customerMergeMoveTables = [
  "customer_contacts",
  "contact_channels",
  "customer_notes",
  "customer_tag_links",
  "quotes",
  "sales_orders",
  "opportunities",
  "sales_activities",
  "customer_notifications",
  "customer_price_overrides",
  "customer_distributor_products",
  "customer_assignments",
  "sales_attachments",
  "quote_sends",
] as const;

/**
 * What moves, as a count per table (spec 09 §3.13, §5's E2-2 items). The
 * preview counts what the merge would move. The merge's `moved`,
 * `customer_merges.moved` and the `sales.customer_merged` payload count what
 * it did. A copied tag link and an added collaborator count as the new row on
 * the winner, which is the row `customer_merge_moves` records (§2.33). Every
 * key is present, 0 when nothing of that kind moves. `sales_attachments`
 * joined with E2-2a2, a response key and an event-payload key added together,
 * which oasdiff reads as additive. A merge recorded before E2-2a2 holds no
 * such key in `customer_merges.moved`, and no route serves that column.
 */
export const CustomerMergeMoveCounts = z
  .object({
    customer_contacts: MergeCount,
    contact_channels: MergeCount,
    customer_notes: MergeCount,
    customer_tag_links: MergeCount,
    quotes: MergeCount,
    sales_orders: MergeCount,
    opportunities: MergeCount,
    sales_activities: MergeCount,
    customer_notifications: MergeCount,
    customer_price_overrides: MergeCount,
    customer_distributor_products: MergeCount,
    customer_assignments: MergeCount,
    sales_attachments: MergeCount,
    quote_sends: MergeCount,
  })
  .strict()
  .openapi("CustomerMergeMoveCounts", {
    description:
      "A count per table of the rows a merge moves to the winner, every key present (spec 09 §2.33, §3.13). A copied tag link and an added collaborator count as the new row on the winner. `sales_attachments` counts the loser's own customer attachments, active or detached, and never a quote's or an order's, which follows its document (E2-2a2). `quote_sends` counts the loser's recorded quote sends, which move so the per-customer agent send guard counts them against the winner (K-3).",
  });

/**
 * What stays on the loser, inert, as a count per table (spec 09 §3.13, §2.30's
 * `left_on_loser`). An override stays when a live one overlaps a live
 * override of the winner on the same product and currency, or when its
 * currency differs from the winner's default currency after the merge. A
 * distributor product stays when the winner's tier after the merge is not
 * `distributor` or the winner already lists the product. Reliability
 * snapshots always stay. The preview counts the distributor products against
 * the winner's current tier, the choice the screen starts on, and the merge
 * counts them again under its locks against the tier it writes. The loser's
 * own assignments and tag links also stay, so its readers can still open the
 * tombstone, and they are not counted here because the winner receives its own
 * rows for them (`CustomerMergeMoveCounts`).
 */
export const CustomerMergeStayCounts = z
  .object({
    customer_notifications: MergeCount,
    customer_price_overrides: MergeCount,
    customer_distributor_products: MergeCount,
    customer_reliability_snapshots: MergeCount,
  })
  .strict()
  .openapi("CustomerMergeStayCounts", {
    description:
      "A count per table of the rows that stay on the loser, inert (spec 09 §3.13, §2.30's `left_on_loser`): finished notifications (sent, delivered, failed or skipped), the record of what was sent under the loser's name (D-334 ruling 6), overrides that overlap the winner's or whose currency differs from the winner's final default currency, distributor products the winner's final tier cannot hold or already lists, and every reliability snapshot.",
  });

/** A row's version: its `xmin` as decimal text (D-318's instrument). */
const MergeVersion = z.string().regex(/^\d+$/).openapi({
  example: "7412093",
  description:
    "The customer row's `xmin` as text, read by the preview and sent back by the merge (D-318's instrument). A version that moved answers 409 `MERGE_STALE`.",
});

/**
 * One customer's profile as the preview serves it, the fields a merge chooses
 * between (spec 09 §3.13's profile list) plus the identity a screen needs to
 * name it. Each field takes the customer's own schema. `legal_name` is served
 * only as far as `LegalNameReach` serves it, and `credit_limit_minor` only
 * when `customerVisibility(ctx.roleCodes, ctx.permissions).creditVisible`
 * holds, the customer card's predicate (§5's E2-2 items, §7). Both are
 * OMITTED otherwise, never null, as on `Customer`.
 */
export const CustomerMergeProfile = z
  .object({
    id: Uuid,
    customer_no: z.string(),
    status: CustomerStatus,
    legal_name: z.string().optional().openapi({
      description:
        "Served only as far as the caller's `LegalNameReach` serves this customer's legal name (#272); absent otherwise.",
    }),
    display_name: z.string(),
    tax_id: z.string().nullable(),
    country: SalesCountry,
    tier: CustomerTier,
    default_currency: SalesCurrency,
    credit_limit_minor: SalesMinorMoneyNonNegative.optional().openapi({
      description:
        "Served only when the customer card would serve it (`customerVisibility(...).creditVisible`, a literal credit role with `sales.read_value`); absent otherwise. A facility_admin clone that holds `sales.read_value` is served none (spec 09 §4.17).",
    }),
    credit_terms_days: z.number().int().min(0).max(180),
    credit_hold: z.boolean(),
    billing_address: SalesAddress,
    shipping_address: SalesAddress,
    preferred_channel: PreferredChannel,
    preferred_locale: PreferredLocale,
  })
  .openapi("CustomerMergeProfile");

/** A user the merge would give a collaborator row on the winner (§3.13). */
export const CustomerMergeAccessGrant = z
  .object({ user_id: Uuid, name: z.string() })
  .openapi("CustomerMergeAccessGrant");

/** A commercial attachment whose audience the merge changes, named only to a
 * holder of `sales.read_value` (§7 narrowing 19). `title` is the attachment's
 * own, null when it has none (§2.29). */
export const CustomerMergeCommercialAttachment = z
  .object({ id: Uuid, title: z.string().nullable() })
  .openapi("CustomerMergeCommercialAttachment");

/** The refusals the preview can see before Confirm (spec 09 §4.17, §9 items
 * 35 and 44). The web disables Confirm while any stands. Each is the merge's
 * own 409 code for the same fact. */
export const CustomerMergeBlocker = z
  .enum(["MERGE_TAX_ID_CONFLICT", "MERGE_CURRENCY_MISMATCH", "MERGE_IMPORT_ORDER_REF_CONFLICT"])
  .openapi("CustomerMergeBlocker");

/** One order reference both customers hold on a live imported order, with
 * each side's order number, so the admin can tell which to cancel (spec 09
 * §4.17, D-359). The merge's 409 names the same pairs. */
export const CustomerMergeImportRefConflict = z
  .object({
    order_ref: z.string(),
    loser_order_no: z.string(),
    winner_order_no: z.string(),
  })
  .openapi("CustomerMergeImportRefConflict");

/** `POST /v1/customers/{id}/merge-preview` (spec 09 §5's E2-2 row 15). The
 * path customer is the winner. */
export const CustomerMergePreviewRequest = z
  .object({ loser_id: Uuid })
  .strict()
  .openapi("CustomerMergePreviewRequest");

/**
 * The preview (spec 09 §3.13, §4.17, §5's E2-2 items). `winner_version` and
 * `loser_version` are each row's version under the names the merge body sends
 * them back as. Exactly one of `commercial_audience_changes` and
 * `commercial_audience_withheld_count` is present: the list for a holder of
 * `sales.read_value`, the count for any other caller. The set holds the
 * loser's own commercial attachments and those of every quote and order that
 * moves (§3.13), since E2-2a2 gave the table its rows.
 */
export const CustomerMergePreview = z
  .object({
    winner: CustomerMergeProfile,
    loser: CustomerMergeProfile,
    winner_version: MergeVersion,
    loser_version: MergeVersion,
    moves: CustomerMergeMoveCounts,
    stays: CustomerMergeStayCounts,
    gains_access: z.array(CustomerMergeAccessGrant).openapi({
      description:
        "Every user who would receive a collaborator row on the winner, the loser's owner and collaborators, whether or not the row widens what that user reads, and never a user who already holds any assignment on the winner (spec 09 §3.13). The merge body splits exactly these users between `add_collaborators` and `skip_collaborators`.",
    }),
    commercial_audience_changes: z.array(CustomerMergeCommercialAttachment).optional().openapi({
      description:
        "Served only to a holder of `sales.read_value` (§7 narrowing 19): the loser's own commercial attachments and those of every quote and order that moves, whose readers become the winner's readers. Absent for any other caller.",
    }),
    commercial_audience_withheld_count: MergeCount.optional().openapi({
      description:
        "Served to a caller without `sales.read_value` in place of `commercial_audience_changes`: the size of that set. Absent for a holder.",
    }),
    blockers: z.array(CustomerMergeBlocker).openapi({
      description:
        "The refusals the merge would give as the rows stand: `MERGE_TAX_ID_CONFLICT` when both tax ids are set and differ, `MERGE_CURRENCY_MISMATCH` when the default currencies differ and the loser holds any quote, order or deal, open or finished (spec 09 §9 item 35), `MERGE_IMPORT_ORDER_REF_CONFLICT` when both customers hold an imported order that is not cancelled with the same `import_order_ref` (§9 item 44, D-353 ruling 5). Empty when nothing blocks.",
    }),
    import_order_ref_conflicts: z.array(CustomerMergeImportRefConflict).optional().openapi({
      description:
        "Each order reference behind `MERGE_IMPORT_ORDER_REF_CONFLICT`, with the loser's and the winner's order numbers, in reference order. Empty when that blocker does not stand. Optional only for a server one deploy behind (D-359).",
    }),
  })
  .openapi("CustomerMergePreview");

/** Which customer's value survives for one profile field. */
export const CustomerMergeSide = z.enum(["winner", "loser"]).openapi("CustomerMergeSide");

/**
 * A winner-or-loser choice for every profile field (spec 09 §3.13's profile
 * list, D-324 ruling 4). Every field is named, so `customer_merges.field_choices`
 * records each one. Three checks the schema cannot make, because they read the
 * stored rows, are the server's (§5's E2-2 row 16): `default_currency` and
 * `credit_limit_minor` come from one customer when the currencies differ (400
 * `merge_credit_currency_split`), the final `country` and `tax_id` pass
 * `taxIdValid` (400 `tax_id_format`), and a `tier` that differs from the
 * winner's needs `sales.tier.manage` (403).
 */
export const CustomerMergeFieldChoices = z
  .object({
    legal_name: CustomerMergeSide,
    display_name: CustomerMergeSide,
    tax_id: CustomerMergeSide,
    country: CustomerMergeSide,
    tier: CustomerMergeSide,
    default_currency: CustomerMergeSide,
    credit_limit_minor: CustomerMergeSide,
    credit_terms_days: CustomerMergeSide,
    credit_hold: CustomerMergeSide,
    billing_address: CustomerMergeSide,
    shipping_address: CustomerMergeSide,
    preferred_channel: CustomerMergeSide,
    preferred_locale: CustomerMergeSide,
  })
  .strict()
  .openapi("CustomerMergeFieldChoices");

/**
 * `POST /v1/customers/{id}/merge` (spec 09 §5's E2-2 row 16). The path
 * customer is the winner. The three lists are required, even when empty, so a
 * body can never leave the access question unanswered. A user named in both
 * collaborator lists is the body's 400 (`merge_collaborator_in_both_lists`),
 * and so is a user named twice in one list (`merge_collaborator_repeated`,
 * D-334 ruling 7): the server compares the lists as sets and then writes one
 * collaborator row per entry, so a repeat would reach the unique
 * `customer_assignments_identity_idx` as a 500. Ids compare case-blind, as
 * the server's set comparison does, so `U` and `u` are one user in either
 * check. `commercial_attachment_count` is read
 * only from a caller without `sales.read_value`, whose `commercial_attachment_ids`
 * is the empty list and is not read; an absent count reads as 0.
 */
export const CustomerMergeRequest = z
  .object({
    loser_id: Uuid,
    fields: CustomerMergeFieldChoices,
    winner_version: MergeVersion,
    loser_version: MergeVersion,
    add_collaborators: z.array(Uuid).max(1000),
    skip_collaborators: z.array(Uuid).max(1000),
    commercial_attachment_ids: z.array(Uuid).max(10000),
    commercial_attachment_count: MergeCount.optional(),
  })
  .strict()
  .superRefine((body, issue) => {
    // One pass per list, each entry judged against the entries before it in
    // its own list (a repeat) and, for the skip list, against the whole add
    // list (both lists). Lower-cased, the comparison the server's `sameSet`
    // makes, so no spelling of one id slips past either check.
    const added = new Set<string>();
    body.add_collaborators.forEach((userId, index) => {
      const id = userId.toLowerCase();
      if (added.has(id)) {
        issue.addIssue({
          code: "custom",
          path: ["add_collaborators", index],
          message: "a user is named once",
          params: { reason: "merge_collaborator_repeated" },
        });
      }
      added.add(id);
    });
    const skipped = new Set<string>();
    body.skip_collaborators.forEach((userId, index) => {
      const id = userId.toLowerCase();
      if (added.has(id)) {
        issue.addIssue({
          code: "custom",
          path: ["skip_collaborators", index],
          message: "a user is either added or skipped, never both",
          params: { reason: "merge_collaborator_in_both_lists" },
        });
      } else if (skipped.has(id)) {
        issue.addIssue({
          code: "custom",
          path: ["skip_collaborators", index],
          message: "a user is named once",
          params: { reason: "merge_collaborator_repeated" },
        });
      }
      skipped.add(id);
    });
  })
  .openapi("CustomerMergeRequest");

/**
 * The merge's answer (spec 09 §5's E2-2 items). It names the winner and nests
 * no `Customer`, because the idempotency store keeps a nested object as sent
 * and the winner's `notes` would stay there (§2.26). The web reads the
 * winner's 360 after the merge.
 */
export const CustomerMergeResult = z
  .object({
    merge_id: Uuid,
    winner_customer_id: Uuid,
    moved: CustomerMergeMoveCounts,
    left_on_loser: CustomerMergeStayCounts,
  })
  .openapi("CustomerMergeResult");
